/**
 * ImportSaleworkAmisModal — TCR-2348: nhập SP hàng loạt từ file Salework + AMIS.
 * 3 bước: 0 chọn file + cấu hình mặc định → 1 xem trước kết quả ghép → 2 kết quả nhập.
 * TCR-2420: thêm chế độ "Chỉ file Salework" (mặc định, nhanh) — không cần file AMIS,
 * ĐVT/Nhóm VTHH bổ sung sau; chế độ ghép Salework + AMIS giữ nguyên hành vi cũ.
 * TCR-2421: thêm chế độ "Chỉ file AMIS" — Mã/Tên/ĐVT/Nhóm lấy từ AMIS, giá bán = 0,
 * barcode/ảnh bổ sung sau (Salework là nguồn của 3 trường đó).
 * Logic thuần (đọc sheet, chia lô, tổng kết, báo cáo) nằm ở ../parts/import-salework-amis-helpers.ts.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Radio, Select, Space, Statistic, Table, Tag, Typography, Upload, Progress, message } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { productService, type ImportRowResult, type ProductImportHistoryRow, type ProductImportHistoryStatus, type ProductImportHistorySummary } from '@/services/product.service';
import { accountingSettingsService } from '@/services/accounting-settings.service';
import { PRODUCT_BRANDS_PAGE_QUERY, useBrandList } from '@/hooks/useBrands';
import { useCategoryOptions } from '@/hooks/useCategories';
import { productKeys } from '@/services/queryKeys';
import {
  buildAmisOnlyRows, buildSaleworkOnlyRows, mergeSaleworkAmis, parseAmisWorkbook, parseSaleworkRows,
  type AmisOnlyResult, type MergeResult, type SaleworkOnlyResult,
} from '../parts/salework-amis-merge';
import { downloadSkippedReport, downloadUnmatchedReport, readSheetRows, readWorkbookSheets } from '../parts/import-salework-amis-helpers';
import { fetchRemainingPages, PRODUCT_IMPORT_MAX_ROWS } from '../parts/import-history-pages';

interface Props { open: boolean; onClose: () => void }

/** Chế độ nhập: solo = chỉ file Salework (TCR-2420); amis = chỉ file AMIS (TCR-2421); full = ghép 2 file (TCR-2348). */
type ImportMode = 'solo' | 'amis' | 'full';

/** Tiêu đề modal theo chế độ — giữ đúng tên file người dùng đang có trên tay. */
const MODE_TITLE: Record<ImportMode, string> = {
  solo: 'Nhập sản phẩm từ Salework',
  amis: 'Nhập sản phẩm từ AMIS',
  full: 'Nhập sản phẩm từ Salework + AMIS',
};

const ERR_AMIS = "File AMIS không đúng định dạng — cần file 'Danh sách hàng hóa, dịch vụ' hoặc bảng 4 cột Mã hàng / Tên hàng / ĐVT / Thương hiệu (Nhóm) xuất từ AMIS";
const ERR_SW = "File Salework không đúng định dạng — cần file 'Kho hàng' xuất từ Salework";

const STATUS_TAG: Record<ImportRowResult['status'], { color: string; text: string }> = {
  created: { color: 'green', text: 'Đã tạo' },
  updated: { color: 'blue', text: 'Đã cập nhật định mức' },
  valid: { color: 'blue', text: 'Hợp lệ' },
  skipped_duplicate: { color: 'gold', text: 'Đã tồn tại' },
  error: { color: 'red', text: 'Lỗi' },
};

interface ItemGroup { _id: string; code: string; name: string; nature?: string }

interface HistoryRunState {
  id: string;
  status: ProductImportHistoryStatus;
  received_rows: number;
  total_rows: number;
  processed_count: number;
  failure_reason?: string;
}

// failure_stage is declared locally so this modal compiles whether or not the
// service's ImportRowResult already carries it; server history rows omit it.
type IndexedImportRowResult = ImportRowResult & { row_index?: number; failure_stage?: 'conversion' | 'bom' };
type ImportInputRowSummary = { is_combo?: boolean; is_bom_material?: boolean };

const processedCount = (history: ProductImportHistorySummary) =>
  history.created_count + history.updated_count + history.error_count + history.skipped_duplicate_count;

function summarizeIndexedResults(results: IndexedImportRowResult[], rows: ImportInputRowSummary[]) {
  const paired = results.map((result, index) => ({ result, row: rows[result.row_index ?? index] }));
  return {
    created: results.filter((result) => result.status === 'created').length,
    updated: results.filter((result) => result.status === 'updated').length,
    existed: results.filter((result) => result.status === 'skipped_duplicate').length,
    error: results.filter((result) => result.status === 'error').length,
    comboCreated: paired.filter(({ result, row }) => row?.is_combo && result.status === 'created').length,
    comboUpdated: paired.filter(({ result, row }) => row?.is_combo && result.status === 'updated').length,
    comboBomError: paired.filter(({ result, row }) => row?.is_combo && result.status === 'error' && result.failure_stage === 'bom').length,
    bomMaterialCreated: paired.filter(({ result, row }) => row?.is_bom_material && result.status === 'created').length,
    bomMaterialExisted: paired.filter(({ result, row }) => row?.is_bom_material && result.status === 'skipped_duplicate').length,
  };
}

const STAGING_ATTEMPTS = 4;
const STAGING_RETRY_BASE_MS = 1000;

/** Network/unavailable (no response), timeouts, lease conflicts, rate limits and 5xx are retryable. */
function isRetryableStagingError(error: unknown): boolean {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status !== 'number' || status === 408 || status === 409 || status === 429 || status >= 500;
}

/** Staged uploads/submit are idempotent by row index, so bounded retries are safe. */
async function withStagingRetry<T>(operation: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= STAGING_ATTEMPTS || !isRetryableStagingError(error)) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, STAGING_RETRY_BASE_MS * 2 ** (attempt - 1)));
    }
  }
}

async function loadAllHistoryRows(historyId: string): Promise<ProductImportHistoryRow[]> {
  const pageSize = 100;
  const first = await productService.listImportHistoryRows(historyId, { page: 1, page_size: pageSize });
  const rest = await fetchRemainingPages(first.total, pageSize, (page) =>
    productService.listImportHistoryRows(historyId, { page, page_size: pageSize }));
  return [first, ...rest].flatMap((page) => page.data);
}

export function ImportSaleworkAmisModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  // Mặc định solo: đa số tenant chỉ có file xuất từ Salework, không có file AMIS.
  const [mode, setMode] = useState<ImportMode>('solo');
  const [swFile, setSwFile] = useState<File | null>(null);
  const [amisFile, setAmisFile] = useState<File | null>(null);
  const [brandId, setBrandId] = useState<string>();
  const [groupId, setGroupId] = useState<string>();
  const [categoryId, setCategoryId] = useState<string>();
  const [bomGroupId, setBomGroupId] = useState<string>();
  const [merge, setMerge] = useState<MergeResult | null>(null);
  const [solo, setSolo] = useState<SaleworkOnlyResult | null>(null);
  const [amisOnly, setAmisOnly] = useState<AmisOnlyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<IndexedImportRowResult[]>([]);
  const [historyRun, setHistoryRun] = useState<HistoryRunState | null>(null);
  const [historyOutcomeRows, setHistoryOutcomeRows] = useState<ProductImportHistoryRow[]>([]);

  const { data: brandsData } = useBrandList(PRODUCT_BRANDS_PAGE_QUERY);
  const { data: groupData } = useQuery({
    queryKey: ['inventory-item-groups'],
    queryFn: () => accountingSettingsService.getInventoryItemGroups(),
    staleTime: 60_000,
  });
  const { options: categoryOptions } = useCategoryOptions();

  const brandOptions = useMemo(() => (brandsData?.data ?? []).map((b) => ({ value: b._id, label: b.name })), [brandsData]);
  const groupOptions = useMemo(
    () => ((groupData?.data ?? []) as ItemGroup[]).map((g) => ({ value: g._id, label: `${g.code} - ${g.name}` })),
    [groupData],
  );
  const rawMaterialGroups = useMemo(
    () => ((groupData?.data ?? []) as ItemGroup[]).filter((g) => g.nature === 'raw_material').map((g) => ({ value: g._id, label: `${g.code} - ${g.name}` })),
    [groupData],
  );

  // Thương hiệu là tuỳ chọn (TCR-2348): BE tự dùng "Không thương hiệu" khi không gửi default_brand_id.
  // Solo (TCR-2420): bắt buộc Danh mục mặc định vì không có Nhóm VTHH của AMIS để khớp danh mục.
  // Amis (TCR-2421): Danh mục mặc định chỉ là phương án dự phòng khi Nhóm VTHH của AMIS không khớp tên danh mục.
  const canContinue = mode === 'solo'
    ? !!swFile && !!groupId && !!categoryId
    : mode === 'amis'
      ? !!amisFile && !!groupId
      : !!swFile && !!amisFile && !!groupId;

  /** Đổi chế độ: bỏ toàn bộ file/kết quả đã đọc của chế độ trước để tránh lẫn dữ liệu. */
  const handleModeChange = (next: ImportMode) => {
    if (next === mode) return;
    setMode(next);
    setSwFile(null); setAmisFile(null); setMerge(null); setSolo(null); setAmisOnly(null); setError(null);
  };

  /** Đóng modal = reset toàn bộ state để lần mở sau bắt đầu sạch (về lại chế độ solo mặc định). */
  const handleClose = () => {
    if (importing) return; // đang ghi DB: không cho đóng giữa chừng
    setStep(0); setMode('solo'); setSwFile(null); setAmisFile(null); setBrandId(undefined); setGroupId(undefined);
    setCategoryId(undefined); setBomGroupId(undefined); setMerge(null); setSolo(null); setAmisOnly(null); setError(null); setResults([]); setHistoryRun(null); setHistoryOutcomeRows([]); setProgress({ done: 0, total: 0 });
    onClose();
  };

  /**
   * Bước 0 → 1: đọc file rồi dựng danh sách dòng import.
   * - solo: chỉ đọc Salework và dựng dòng trực tiếp (không ghép).
   * - amis: chỉ đọc AMIS và dựng dòng trực tiếp — không có giá/barcode/ảnh.
   * - full: đọc cả 2 file rồi ghép theo mã; file sai định dạng → báo lỗi rõ file nào sai.
   */
  const handleContinue = async () => {
    setParsing(true); setError(null);
    try {
      if (mode === 'solo') {
        if (!swFile) return;
        const sw = parseSaleworkRows(await readSheetRows(swFile, 'KHO_HANG'));
        setSolo(buildSaleworkOnlyRows(sw));
      } else if (mode === 'amis') {
        if (!amisFile) return;
        // Không ép sheet Salework: file AMIS có thể chỉ có 1 sheet tuỳ bản xuất.
        const amis = parseAmisWorkbook(await readWorkbookSheets(amisFile));
        setAmisOnly(buildAmisOnlyRows(amis));
      } else {
        if (!swFile || !amisFile) return;
        const amis = parseAmisWorkbook(await readWorkbookSheets(amisFile));
        const sw = parseSaleworkRows(await readSheetRows(swFile, 'KHO_HANG'));
        setMerge(mergeSaleworkAmis(sw, amis));
      }
      setStep(1);
    } catch (e) {
      const m = (e as Error)?.message;
      setError(m === 'FILE_AMIS_INVALID' ? ERR_AMIS : m === 'FILE_SALEWORK_INVALID' ? ERR_SW : 'Không đọc được file — hãy kiểm tra lại file .xlsx');
    } finally {
      setParsing(false);
    }
  };

  /** Dòng sẽ gửi BE theo chế độ đang chọn — một nguồn duy nhất cho cả nút "Nhập N" và handleImport. */
  const importRows = (mode === 'solo' ? solo?.rows : mode === 'amis' ? amisOnly?.rows : merge?.rows) ?? [];
  const n = importRows.length;
  const bomMaterialCount = (mode === 'amis' ? amisOnly?.bomMaterialSkuCount : mode === 'full' ? merge?.rows.filter((r) => r.is_bom_material).length : 0) ?? 0;
  const bomErrors = importRows.flatMap((row) => row.bom_errors ?? []);
  const hasCombo = importRows.some((row) => row.is_combo);
  const canImport = n > 0 && !bomErrors.length && (!bomMaterialCount || (!!bomGroupId && !!categoryId));
  useEffect(() => {
    if (bomMaterialCount && rawMaterialGroups.length === 1 && !bomGroupId) setBomGroupId(rawMaterialGroups[0].value);
  }, [bomMaterialCount, rawMaterialGroups, bomGroupId]);

  /** Show a terminal (completed/failed) run from its persisted backend outcomes. */
  const applyTerminalHistory = useCallback((history: ProductImportHistorySummary, rows: ProductImportHistoryRow[]) => {
    setError(null);
    setHistoryOutcomeRows(rows);
    setResults(rows.map((row) => ({ row_index: row.row_index, sku: row.sku, status: row.status, product_id: row.product_id, message: row.message })));
    setHistoryRun({
      id: history.history_id,
      status: history.status,
      received_rows: history.received_rows,
      total_rows: history.total_rows,
      processed_count: processedCount(history),
      failure_reason: history.failure_reason,
    });
    if (rows.some((row) => row.status === 'created' || row.status === 'updated')) {
      void qc.invalidateQueries({ queryKey: productKeys.all });
    }
  }, [qc]);

  /** Bước 1 → 2: stage normalized inputs, submit once, then follow backend-owned processing. */
  const handleImport = async () => {
    const rows = importRows;
    if (!rows.length || !groupId || !canImport) return;
    if (rows.length > PRODUCT_IMPORT_MAX_ROWS) {
      const text = `Mỗi lượt import tối đa ${PRODUCT_IMPORT_MAX_ROWS} dòng (file hiện có ${rows.length} dòng); vui lòng tách file.`;
      setError(text);
      message.error(text);
      return;
    }
    // Parser-only validation metadata must never reach the strict BE row DTO.
    const outboundRows = rows.map(({ bom_errors, ...row }) => {
      void bom_errors;
      return row;
    });
    setStep(2); setImporting(true); setResults([]); setError(null); setHistoryRun(null); setProgress({ done: 0, total: rows.length });
    let historyId: string | undefined;
    try {
      const source = mode === 'solo' ? 'salework' : mode === 'amis' ? 'amis' : 'salework_amis';
      const defaults = {
        inventory_item_group_id: groupId,
        ...(brandId ? { default_brand_id: brandId } : {}),
        ...(categoryId ? { default_category_id: categoryId } : {}),
        ...(bomGroupId ? { bom_material_inventory_item_group_id: bomGroupId } : {}),
      };
      const started = await productService.startImportHistory({ source, total_rows: rows.length, ...defaults });
      historyId = started.history_id;
      setHistoryRun({
        id: historyId,
        status: 'uploading',
        received_rows: 0,
        total_rows: rows.length,
        processed_count: 0,
      });
      void qc.invalidateQueries({ queryKey: ['products', 'import-history', 'active-observer'] });
      for (let startIndex = 0; startIndex < outboundRows.length; startIndex += 100) {
        const chunk = outboundRows.slice(startIndex, startIndex + 100);
        const uploaded = await withStagingRetry(() =>
          productService.uploadImportHistoryRows(started.history_id, { start_index: startIndex, rows: chunk }));
        setProgress({ done: uploaded.received_rows, total: uploaded.total_rows });
        setHistoryRun((current) => current ? { ...current, received_rows: uploaded.received_rows } : current);
      }
      const submitted = await withStagingRetry(() => productService.submitImportHistory(started.history_id));
      setProgress({ done: submitted.received_rows, total: submitted.total_rows });
      setHistoryRun({
        id: started.history_id,
        status: submitted.status,
        received_rows: submitted.received_rows,
        total_rows: submitted.total_rows,
        processed_count: 0,
      });
    } catch (e) {
      const detail = (e as Error)?.message || 'Lỗi không xác định';
      if (!historyId) {
        const text = `Không thể bắt đầu lượt import; chưa gửi sản phẩm. ${detail}`;
        setError(text);
        message.error(text);
        return;
      }
      const failedHistoryId = historyId;
      // Staging gave up. If the server already queued the run (e.g. a lost submit
      // response) keep following it; otherwise stop it so it never stays `uploading`.
      const before = await productService.getImportHistory(failedHistoryId).catch(() => null);
      let summary = before;
      if (!before || before.status === 'uploading') {
        summary = await productService.abortImportHistoryUpload(failedHistoryId).catch(() => null);
      }
      // Only a run the server confirmed as still uploading is known to have no product writes.
      const stoppedWhileUploading = before?.status === 'uploading' && summary?.status === 'failed';
      if (summary && !stoppedWhileUploading && (summary.status === 'completed' || summary.status === 'failed')) {
        // The run already reached a terminal state server-side: show its real outcomes.
        const outcomeRows = await loadAllHistoryRows(summary.history_id).catch(() => null);
        if (outcomeRows) {
          applyTerminalHistory(summary, outcomeRows);
          return;
        }
        setHistoryRun({
          id: summary.history_id,
          status: summary.status,
          received_rows: summary.received_rows,
          total_rows: summary.total_rows,
          processed_count: processedCount(summary),
          failure_reason: summary.failure_reason,
        });
        const text = `Lượt import ${failedHistoryId} đã kết thúc nhưng chưa tải được kết quả; xem chi tiết trong Lịch sử import.`;
        setError(text);
        message.warning(text);
        return;
      }
      if (summary && summary.status !== 'uploading' && summary.status !== 'failed') {
        setError(null);
        setHistoryRun({
          id: summary.history_id,
          status: summary.status,
          received_rows: summary.received_rows,
          total_rows: summary.total_rows,
          processed_count: processedCount(summary),
          failure_reason: summary.failure_reason,
        });
        return;
      }
      const text = stoppedWhileUploading
        ? `Tải dữ liệu lên thất bại sau ${STAGING_ATTEMPTS} lần thử; lượt import ${failedHistoryId} đã dừng và chưa ghi sản phẩm nào. Vui lòng import lại. ${detail}`
        : `Tải dữ liệu lên thất bại sau ${STAGING_ATTEMPTS} lần thử; chưa xác nhận được trạng thái lượt import ${failedHistoryId} trên máy chủ, xem lại trong Lịch sử import. ${detail}`;
      setHistoryRun((current) => ({
        id: failedHistoryId,
        status: 'failed',
        received_rows: summary?.received_rows ?? current?.received_rows ?? 0,
        total_rows: summary?.total_rows ?? current?.total_rows ?? rows.length,
        processed_count: 0,
        failure_reason: summary?.failure_reason ?? (summary ? undefined : 'Không dừng được lượt import trên máy chủ; lượt sẽ tự hết hạn.'),
      }));
      setError(text);
      message.error(text);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!historyRun || !['uploading', 'queued', 'processing'].includes(historyRun.status)) return;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const history = await productService.getImportHistory(historyRun.id);
          if (cancelled) return;
          if (history) {
            const processed = processedCount(history);
            if (history.status === 'completed' || history.status === 'failed') {
              const rows = await loadAllHistoryRows(historyRun.id);
              if (cancelled) return;
              applyTerminalHistory(history, rows);
              return;
            }
            setHistoryRun({
              id: history.history_id,
              status: history.status,
              received_rows: history.received_rows,
              total_rows: history.total_rows,
              processed_count: processed,
              failure_reason: history.failure_reason,
            });
          }
        } catch {
          // Keep polling: a transient summary failure must not change the server-owned run state.
        }
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
      }
    };
    void poll();
    return () => { cancelled = true; };
  }, [historyRun?.id, historyRun?.status, applyTerminalHistory]);

  const fileUpload = (testId: string, title: string, hint: string, file: File | null, set: (f: File | null) => void) => (
    <div data-testid={testId}>
      <Upload.Dragger
        accept=".xlsx" maxCount={1} fileList={file ? [{ uid: file.name, name: file.name, status: 'done' } as never] : []}
        beforeUpload={(f) => { set(f); setError(null); return false; }}
        onRemove={() => set(null)}
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">{title}</p>
        <p className="ant-upload-hint">{hint}</p>
      </Upload.Dragger>
    </div>
  );

  const sum = summarizeIndexedResults(results, importRows);
  const resultRows = (historyOutcomeRows.length ? historyOutcomeRows : results.map((result, index) => ({ ...result, row_index: index }))).map((result) => {
    const row = importRows[result.row_index];
    const rowType = row?.is_combo ? 'combo'
      : row?.is_bom_material ? 'bom_material'
        : 'product';
    const description = rowType === 'combo'
      ? result.status === 'created'
        ? 'Combo · BOM đã lưu'
        : result.status === 'error' && result.product_id
          ? 'Combo · BOM lỗi'
          : 'Combo'
      : rowType === 'bom_material' ? 'Vật tư từ BOM' : 'Sản phẩm';
    return { ...result, _k: `${result.row_index}-${result.sku}`, rowType, description };
  });

  const bomPreview = bomMaterialCount > 0 || hasCombo || bomErrors.length > 0 ? (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space size={32} wrap>
        <Statistic title="Combo" value={importRows.filter((row) => row.is_combo).length} />
        <Statistic title="BOM" value={importRows.filter((row) => row.is_combo && row.bom_components?.length).length} />
        <Statistic title="Component" value={importRows.reduce((count, row) => count + (row.bom_components?.length ?? 0), 0)} />
        <Statistic title="Vật tư lấy từ BOM" value={bomMaterialCount} />
        <Statistic title="BOM lỗi" value={bomErrors.length} />
      </Space>
      {hasCombo && <Tag color="purple">Nhóm combo: FINISHED_GOODS - Thành phẩm</Tag>}
      {bomMaterialCount > 0 && <Space wrap>
        <Select aria-label="Nhóm VTHH vật tư BOM" placeholder="Nhóm VTHH vật tư BOM (bắt buộc)" style={{ width: 300 }} options={rawMaterialGroups} value={bomGroupId} onChange={setBomGroupId} />
        <Select aria-label="Danh mục mặc định vật tư BOM" placeholder="Danh mục mặc định vật tư BOM (bắt buộc)" style={{ width: 300 }} options={categoryOptions} value={categoryId} onChange={(v) => setCategoryId(v ?? undefined)} />
      </Space>}
      {bomMaterialCount > 0 && !rawMaterialGroups.length && <Alert type="warning" message="Không có nhóm VTHH có tính chất nguyên vật liệu" />}
      {bomErrors.length > 0 && <Alert type="error" message={`Có ${bomErrors.length} lỗi BOM cần xử lý`} description={bomErrors.join('; ')} />}
    </Space>
  ) : null;

  return (
    <Modal open={open} onCancel={handleClose}
      title={MODE_TITLE[mode]}
      width={860} destroyOnClose maskClosable={false}
      footer={
        step === 0 ? [
          <Button key="c" onClick={handleClose}>Huỷ</Button>,
          <Button key="n" type="primary" disabled={!canContinue} loading={parsing} onClick={() => void handleContinue()}>Tiếp tục</Button>,
        ] : step === 1 ? [
          <Button key="b" onClick={() => setStep(0)}>Quay lại</Button>,
          <Button key="go" type="primary" disabled={!canImport} onClick={() => void handleImport()}>{`Nhập ${n} sản phẩm`}</Button>,
        ] : [
          <Button key="x" type="primary" loading={importing} onClick={handleClose}>Đóng</Button>,
        ]
      }
    >
      {step === 0 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div data-testid="import-mode">
            <Typography.Text strong>Chế độ nhập</Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Radio.Group value={mode} disabled={parsing} onChange={(e) => handleModeChange(e.target.value as ImportMode)}>
                <Radio value="solo">Chỉ file Salework (nhanh)</Radio>
                <Radio value="amis">Chỉ file AMIS</Radio>
                <Radio value="full">Salework + AMIS (đầy đủ)</Radio>
              </Radio.Group>
            </div>
          </div>
          {mode === 'solo' ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Chỉ cần file Kho hàng xuất từ Salework: Mã, Tên, Giá bán, Barcode, Ảnh lấy từ Salework;
              ĐVT và Nhóm VTHH bổ sung sau khi nhập.
            </Typography.Paragraph>
          ) : mode === 'amis' ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Chỉ cần file xuất từ AMIS — "Danh sách hàng hóa, dịch vụ" hoặc bảng 4 cột Mã hàng / Tên hàng / ĐVT / Thương hiệu (Nhóm):
              Mã, Tên, ĐVT, Nhóm VTHH lấy từ AMIS; Giá bán, Barcode, Ảnh bổ sung sau khi nhập.
            </Typography.Paragraph>
          ) : (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Chọn file xuất từ Salework và file từ AMIS ("Danh sách hàng hóa, dịch vụ" hoặc bảng 4 cột Mã hàng / Tên hàng / ĐVT / Thương hiệu).
              Chỉ những mã có ở CẢ HAI file mới được nhập: Mã, Tên, ĐVT, Nhóm lấy từ AMIS; Giá bán, Barcode, Ảnh lấy từ Salework.
            </Typography.Paragraph>
          )}
          {mode === 'solo' && (
            <Alert type="info" showIcon
              message="Chế độ này không có ĐVT/Nhóm từ AMIS — sản phẩm sẽ vào Danh mục mặc định, không có ĐVT; bổ sung sau." />
          )}
          {mode === 'amis' && (
            <Alert type="info" showIcon
              message="Chế độ này không có Giá bán/Barcode/Ảnh từ Salework — giá bán = 0, bổ sung sau." />
          )}
          {error && <Alert type="error" showIcon message={error} />}
          <Space align="start" size={16} style={{ width: '100%' }} wrap>
            {mode !== 'amis' && (
              <div style={{ flex: 1, minWidth: 320 }}>
                {fileUpload('upload-salework', 'File Salework (.xlsx)', 'File Salework: Kho hàng → Xuất file thông tin sản phẩm', swFile, setSwFile)}
              </div>
            )}
            {mode !== 'solo' && (
              <div style={{ flex: 1, minWidth: 320 }}>
                {fileUpload('upload-amis', 'File AMIS (.xlsx)', 'File AMIS: "Danh sách hàng hóa, dịch vụ" hoặc bảng 4 cột Mã hàng / Tên hàng / ĐVT / Thương hiệu (Nhóm)', amisFile, setAmisFile)}
              </div>
            )}
          </Space>
          <Space wrap size={16}>
            <Select aria-label="Thương hiệu mặc định" placeholder="Thương hiệu mặc định (tuỳ chọn)" style={{ width: 260 }} allowClear showSearch optionFilterProp="label"
              options={brandOptions} value={brandId} onChange={(v) => setBrandId(v ?? undefined)} />
            <Select aria-label="Nhóm VTHH kế toán" placeholder="Nhóm VTHH kế toán (bắt buộc)" style={{ width: 260 }} showSearch optionFilterProp="label"
              options={groupOptions} value={groupId} onChange={setGroupId} />
            <Select aria-label="Danh mục mặc định" placeholder={mode === 'solo' ? 'Danh mục mặc định (bắt buộc)' : 'Danh mục mặc định (tuỳ chọn)'}
              style={{ width: 260 }} allowClear showSearch optionFilterProp="label"
              options={categoryOptions} value={categoryId} onChange={setCategoryId} />
          </Space>
          <Typography.Text type="secondary">
            {mode === 'solo'
              ? 'Danh mục mặc định: bắt buộc — sản phẩm được gán vào danh mục này (không có Nhóm VTHH từ AMIS để khớp).'
              : 'Danh mục mặc định: dùng khi Nhóm VTHH của AMIS không trùng tên danh mục trong hệ thống.'}
            {' '}Thương hiệu mặc định: để trống thì sản phẩm không khớp thương hiệu sẽ vào "Không thương hiệu".
            {!canContinue && (mode === 'solo'
              ? ' Cần chọn file Salework, nhóm VTHH và danh mục mặc định để tiếp tục.'
              : mode === 'amis'
                ? ' Cần chọn file AMIS và nhóm VTHH để tiếp tục.'
                : ' Cần chọn đủ 2 file và nhóm VTHH để tiếp tục.')}
          </Typography.Text>
        </Space>
      )}

      {step === 1 && mode === 'full' && merge && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {bomPreview}
          <Space size={48} wrap>
            <Statistic title="sản phẩm sẽ được nhập" value={n} />
            <Statistic title="mã chỉ có ở Salework (bỏ qua)" value={merge.unmatched.length} />
            <Statistic title="mã AMIS trùng đã gộp" value={merge.duplicatesDropped.length} />
          </Space>
          {n === 0 && <Alert type="warning" showIcon message="Không có mã nào khớp giữa 2 file" />}
          <Button icon={<DownloadOutlined />} onClick={() => downloadUnmatchedReport(merge)}>Tải báo cáo không khớp</Button>
          <Table size="small" rowKey="sku" dataSource={merge.rows} pagination={{ pageSize: 8, showSizeChanger: false }}
            columns={[
              { title: 'Mã', dataIndex: 'sku' }, { title: 'Tên', dataIndex: 'name' }, { title: 'ĐVT', dataIndex: 'unit' },
              { title: 'Loại', render: (_: unknown, row: (typeof merge.rows)[number]) => row.is_bom_material
                ? <Tag color="cyan">Vật tư từ BOM</Tag>
                : row.is_combo ? <Tag color="purple">Combo · {row.bom_components?.length ?? 0} component</Tag> : 'Sản phẩm' },
              { title: 'Nhóm', dataIndex: 'category' },
              { title: 'Giá', dataIndex: 'price', align: 'right', render: (v: number) => v.toLocaleString('vi-VN') },
            ]} />
        </Space>
      )}

      {step === 1 && mode === 'amis' && amisOnly && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {bomPreview}
          <Space size={48} wrap>
            <Statistic title="sản phẩm sẽ được nhập" value={n} />
            <Statistic title="dòng bị bỏ qua" value={amisOnly.skipped.length} />
            <Statistic title="mã AMIS trùng đã gộp" value={amisOnly.duplicatesDropped.length} />
          </Space>
          {n === 0 && <Alert type="warning" showIcon message="Không có dòng nào đủ dữ kiện để nhập" />}
          {amisOnly.skipped.length > 0 && (
            <Button icon={<DownloadOutlined />} onClick={() => downloadSkippedReport(amisOnly.skipped)}>Tải báo cáo dòng bị bỏ qua</Button>
          )}
          <Table size="small" rowKey="sku" dataSource={amisOnly.rows} pagination={{ pageSize: 8, showSizeChanger: false }}
            columns={[
              { title: 'Mã', dataIndex: 'sku' }, { title: 'Tên', dataIndex: 'name' },
              { title: 'Loại', render: (_: unknown, row: (typeof amisOnly.rows)[number]) => row.is_bom_material
                ? <Tag color="cyan">Vật tư từ BOM</Tag>
                : row.is_combo ? <Tag color="purple">Combo · {row.bom_components?.length ?? 0} component</Tag> : 'Sản phẩm' },
              { title: 'ĐVT', dataIndex: 'unit' }, { title: 'Nhóm', dataIndex: 'category' },
            ]} />
        </Space>
      )}

      {step === 1 && mode === 'solo' && solo && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space size={48} wrap>
            <Statistic title="sản phẩm sẽ được nhập" value={n} />
            <Statistic title="dòng bị bỏ qua" value={solo.skipped.length} />
          </Space>
          {n === 0 && <Alert type="warning" showIcon message="Không có dòng nào đủ dữ kiện để nhập" />}
          {solo.skipped.length > 0 && (
            <Button icon={<DownloadOutlined />} onClick={() => downloadSkippedReport(solo.skipped)}>Tải báo cáo dòng bị bỏ qua</Button>
          )}
          <Table size="small" rowKey="sku" dataSource={solo.rows} pagination={{ pageSize: 8, showSizeChanger: false }}
            columns={[
              { title: 'Mã', dataIndex: 'sku' }, { title: 'Tên', dataIndex: 'name' },
              { title: 'Giá', dataIndex: 'price', align: 'right', render: (v: number) => v.toLocaleString('vi-VN') },
            ]} />
        </Space>
      )}

      {step === 2 && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {error && <Alert type="error" showIcon message={error} />}
          {importing
            ? <Progress percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} format={() => `đã tải lên ${progress.done}/${progress.total}`} />
            : historyRun && (historyRun.status === 'uploading' || historyRun.status === 'queued' || historyRun.status === 'processing')
              ? <Progress percent={historyRun.total_rows ? Math.round(((historyRun.status === 'uploading' ? historyRun.received_rows : historyRun.processed_count) / historyRun.total_rows) * 100) : 0}
                format={() => `${historyRun.status === 'uploading' ? 'đã tải lên' : historyRun.status === 'queued' ? 'đang chờ xử lý' : 'đã xử lý'} ${historyRun.status === 'uploading' ? historyRun.received_rows : historyRun.processed_count}/${historyRun.total_rows} · đã nhận ${historyRun.received_rows}/${historyRun.total_rows}`} />
              : (
              <>
                {historyRun?.status === 'completed' && <Typography.Text type="secondary">Lịch sử import đã hoàn tất.</Typography.Text>}
                {historyRun?.status === 'failed' && <Alert type="error" showIcon message="Lượt import thất bại" description={historyRun.failure_reason || 'Backend không cung cấp lý do thất bại.'} />}
                {(historyRun?.status === 'completed' || historyRun?.status === 'failed') && <>
                  <Alert type={sum.error > 0 || historyRun.status === 'failed' ? 'warning' : 'success'} showIcon message={`Đã tạo ${sum.created} · Đã cập nhật ${sum.updated} · Đã tồn tại ${sum.existed} · Lỗi ${sum.error}`} />
                  {(hasCombo || bomMaterialCount > 0) && <Space direction="vertical" size={4}>
                    {hasCombo && <Typography.Text>Combo tạo thành công {sum.comboCreated} · Đã cập nhật định mức {sum.comboUpdated} · Lỗi lưu BOM {sum.comboBomError}</Typography.Text>}
                    {bomMaterialCount > 0 && <Typography.Text>Vật tư BOM mới {sum.bomMaterialCreated} · Đã tồn tại {sum.bomMaterialExisted}</Typography.Text>}
                  </Space>}
                  <Typography.Text type="secondary">Sản phẩm mới ở trạng thái Nháp — rà soát rồi bật bán.</Typography.Text>
                </>}
              </>
            )}
          <Table size="small" rowKey="_k" dataSource={resultRows} pagination={{ pageSize: 8, showSizeChanger: false }}
            columns={[
              { title: 'Mã', dataIndex: 'sku' },
              { title: 'Loại', dataIndex: 'description' },
              { title: 'Trạng thái', dataIndex: 'status', render: (s: ImportRowResult['status']) => <Tag color={STATUS_TAG[s].color}>{STATUS_TAG[s].text}</Tag> },
              { title: 'Ghi chú', dataIndex: 'message' },
              { title: 'Mã sản phẩm', dataIndex: 'product_id' },
            ]} />
        </Space>
      )}
    </Modal>
  );
}
